import React, { useEffect, useRef } from 'react';
import * as LightweightCharts from 'lightweight-charts';
import type { MarketIndexCandle } from '../../services/api';

const CandlestickSeries = (LightweightCharts as any).CandlestickSeries;
const createChart = LightweightCharts.createChart;
const ColorType = LightweightCharts.ColorType;
const LineStyle = LightweightCharts.LineStyle;

const CHART_HEIGHT = 128;

export interface IndexMiniChartProps {
  candles: MarketIndexCandle[];
  previousClose: number | null;
  lastPrice: number | null;
  height?: number;
  className?: string;
}

const IndexMiniChart: React.FC<IndexMiniChartProps> = ({
  candles,
  previousClose,
  lastPrice,
  height = CHART_HEIGHT,
  className = '',
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<LightweightCharts.IChartApi | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !candles.length) return;

    let cancelled = false;
    let chart: LightweightCharts.IChartApi | null = null;
    let ro: ResizeObserver | null = null;

    const mountChart = () => {
      if (cancelled || !wrapRef.current) return;
      const el = wrapRef.current;
      const w = Math.floor(el.getBoundingClientRect().width);
      const chartW = Math.max(80, w);
      if (chartW < 80) return;

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      chart = createChart(el, {
        width: chartW,
        height,
        autoSize: false,
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: '#8b9199',
          fontSize: 9,
          fontFamily: 'Inter, system-ui, sans-serif',
        },
        grid: {
          vertLines: { color: 'rgba(255,255,255,0.06)', style: LightweightCharts.LineStyle.Dotted },
          horzLines: { color: 'rgba(255,255,255,0.06)', style: LightweightCharts.LineStyle.Dotted },
        },
        crosshair: { mode: 0 },
        rightPriceScale: {
          borderVisible: false,
          scaleMargins: { top: 0.1, bottom: 0.15 },
        },
        timeScale: {
          borderVisible: false,
          fixLeftEdge: true,
          fixRightEdge: true,
          tickMarkFormatter: (t: number) =>
            new Date(t * 1000).toLocaleDateString('en-US', {
              timeZone: 'UTC',
              month: 'short',
              day: 'numeric',
            }),
        },
        localization: {
          priceFormatter: (p: number) => p.toLocaleString(undefined, { maximumFractionDigits: 2 }),
        },
      });

      const series = chart.addSeries(CandlestickSeries, {
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
      });

      const data = candles.map((c) => ({
        time: c.t as LightweightCharts.UTCTimestamp,
        open: c.o,
        high: c.h,
        low: c.l,
        close: c.c,
      }));
      series.setData(data);

      if (previousClose != null && Number.isFinite(previousClose)) {
        series.createPriceLine({
          price: previousClose,
          color: 'rgba(239, 68, 68, 0.75)',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
        });
      }

      if (lastPrice != null && Number.isFinite(lastPrice)) {
        series.createPriceLine({
          price: lastPrice,
          color: '#f2ca50',
          lineWidth: 2,
          lineStyle: LineStyle.Solid,
          axisLabelVisible: true,
          title: '',
        });
      }

      chart.timeScale().fitContent();
      chartRef.current = chart;

      ro = new ResizeObserver(() => {
        if (!wrapRef.current || !chartRef.current) return;
        const nw = Math.floor(wrapRef.current.getBoundingClientRect().width);
        if (nw >= 80) {
          chartRef.current.applyOptions({ width: nw, height });
        }
      });
      ro.observe(el);
    };

    // Wait until flex layout has assigned a width (clientWidth is often 0 on first paint).
    let innerRafId = 0;
    const outerRafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(mountChart);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(outerRafId);
      if (innerRafId) cancelAnimationFrame(innerRafId);
      ro?.disconnect();
      chartRef.current?.remove();
      chartRef.current = null;
    };
  }, [candles, previousClose, lastPrice, height]);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        width: '100%',
        height,
        maxHeight: height,
        minHeight: height,
        overflow: 'hidden',
        position: 'relative',
        contain: 'strict',
      }}
    />
  );
};

export default IndexMiniChart;
